import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import { type UseFormReturn, useWatch } from 'react-hook-form';
import type { CustomQuestionsFormData } from '../types';
import { suggestButtonValue } from '../utils/label-to-identifier';

/**
 * Fills each button's saved value from its label until the author edits the value. A value is
 * suggested again only when its own label changes, so a drag or a delete never rewrites the values
 * of buttons nobody touched.
 */
const useAutoButtonValues = t0 => {
  const $ = _c(12);
  const {
    control,
    setValue,
    getFieldState
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["choice.saveValue", "choice.buttons"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== control) {
    t2 = {
      control,
      name: t1
    };
    $[1] = control;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [saveValue, buttons] = useWatch(t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = new Map();
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const lastSuggestions = useRef(t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = new Map();
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const suggestedFor = useRef(t4);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = new Set();
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const authoredIds = useRef(t5);
  let t6;
  let t7;
  if ($[6] !== buttons || $[7] !== getFieldState || $[8] !== saveValue || $[9] !== setValue) {
    t6 = () => {
      const values = buttons.map(_temp);
      buttons.forEach((button_0, index) => {
        if (authoredIds.current.has(button_0.id)) {
          return;
        }
        if (!lastSuggestions.current.has(button_0.id)) {
          if (button_0.value) {
            authoredIds.current.add(button_0.id);
            return;
          }
          lastSuggestions.current.set(button_0.id, "");
        }
        if (!saveValue) {
          return;
        }
        if (button_0.value !== lastSuggestions.current.get(button_0.id)) {
          authoredIds.current.add(button_0.id);
          return;
        }
        if (suggestedFor.current.get(button_0.id) === button_0.label) {
          return;
        }
        const taken = new Set(values.filter((value, other) => other !== index && value));
        const suggestion = suggestButtonValue(button_0.label, taken);
        suggestedFor.current.set(button_0.id, button_0.label);
        lastSuggestions.current.set(button_0.id, suggestion);
        values[index] = suggestion;
        if (button_0.value === suggestion) {
          return;
        }
        const name = `choice.buttons.${index}.value` as const;
        setValue(name, suggestion, {
          shouldDirty: true,
          shouldValidate: !!getFieldState(name).error
        });
      });
    };
    t7 = [saveValue, buttons, setValue, getFieldState];
    $[6] = buttons;
    $[7] = getFieldState;
    $[8] = saveValue;
    $[9] = setValue;
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  useEffect(t6, t7);
};
export default useAutoButtonValues;
function _temp(button) {
  return button.value;
}